import { Button, Modal } from 'antd';
import { ArrowRight, FileDown, FileText, Wallet } from 'lucide-react';
import { useState } from 'react';
import {
  useGetOfferLetterDetailsQuery,
  useUpdateOfferLetterStatusMutation,
} from '../../../app/features/recruitment/recruitment.api';
import type {
  TOfferLetterItem,
  TOfferLetterStatus,
} from '../../../app/features/recruitment/recruitment.types';
import WithLoader from '../../../components/WithLoader';
import useAuth from '../../../hooks/useAuth';
import cn from '../../../lib/cn';
import { getErrorMessage } from '../../../lib/error';
import { toast } from '../../../lib/toast';
import { formatDate } from '../../../utils/date';
import { OrgAvatar, panelClass } from './portal';
import { formatExperience, formatSalary } from './portal.utils';

type Props = { offer: TOfferLetterItem };

const STATUS: Record<
  TOfferLetterStatus,
  { label: string; className: string; dot: string; note: string }
> = {
  pending: {
    label: 'Awaiting response',
    className: 'bg-amber-50 text-amber-700 ring-amber-600/15',
    dot: 'bg-amber-500',
    note: 'Review the letter and let them know your decision.',
  },
  accepted: {
    label: 'Accepted',
    className: 'bg-emerald-50 text-emerald-700 ring-emerald-600/15',
    dot: 'bg-emerald-500',
    note: 'You accepted this offer.',
  },
  canceled: {
    label: 'Declined',
    className: 'bg-gray-100 text-gray-600 ring-gray-500/15',
    dot: 'bg-gray-400',
    note: 'You declined this offer.',
  },
};

const StatusBadge = ({ status }: { status: TOfferLetterStatus }) => {
  const cfg = STATUS[status] ?? STATUS.pending;
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset',
        cfg.className,
      )}
    >
      <span className={cn('h-1.5 w-1.5 rounded-full', cfg.dot)} />
      {cfg.label}
    </span>
  );
};

const Stat = ({ label, value }: { label: string; value: string }) => (
  <div className="min-w-0 px-4 py-3">
    <dt className="text-[11px] font-medium uppercase tracking-wider text-gray-400">
      {label}
    </dt>
    <dd className="mt-1 truncate text-sm font-semibold text-gray-900">
      {value}
    </dd>
  </div>
);

export const OfferLetterCard = ({ offer }: Props) => {
  const { verifiedInfo } = useAuth();
  const [modalOpen, setModalOpen] = useState(false);
  const isPending = offer.offer_letter_status === 'pending';

  const { data, isLoading: isFetchingDetails } = useGetOfferLetterDetailsQuery(
    {
      applicant_id: verifiedInfo?.id as string,
      offer_letter_id: offer.offer_letter_id,
    },
    { skip: !modalOpen || !verifiedInfo?.id },
  );

  const [updateStatus, { isLoading: isUpdating }] =
    useUpdateOfferLetterStatusMutation();

  const details = data?.data;

  const handleStatusUpdate = async (status: 'accepted' | 'canceled') => {
    try {
      const res = await updateStatus({
        applicant_id: verifiedInfo?.id as string,
        offer_letter_id: offer.offer_letter_id,
        offer_letter_status: status,
      }).unwrap();

      if (res.success) {
        toast('success', res.message || `Offer letter ${status}`);
        setModalOpen(false);
      } else {
        toast('error', res.message || 'Failed to update status');
      }
    } catch (err) {
      toast('error', getErrorMessage(err, 'Something went wrong'));
    }
  };

  return (
    <>
      {/* Card */}
      <article
        onClick={() => setModalOpen(true)}
        className={cn(
          panelClass,
          'group relative flex cursor-pointer flex-col overflow-hidden transition-colors hover:border-theme-primary/40',
        )}
      >
        {/* Letterhead accent — strongest while the offer needs an answer */}
        <span
          aria-hidden
          className={cn(
            'h-1 w-full',
            isPending
              ? 'bg-gradient-to-r from-theme-primary to-theme-primary/40'
              : offer.offer_letter_status === 'accepted'
                ? 'bg-emerald-500/70'
                : 'bg-gray-200',
          )}
        />

        <div className="flex-1 p-5 sm:p-6">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 gap-3.5">
              <OrgAvatar name={offer.organization_name} />
              <div className="min-w-0">
                <p className="text-xs font-medium text-gray-400">
                  Offer letter from
                </p>
                <h3 className="mt-0.5 truncate text-base font-semibold text-gray-900 transition-colors group-hover:text-theme-primary">
                  {offer.organization_name}
                </h3>
              </div>
            </div>
            <StatusBadge status={offer.offer_letter_status} />
          </div>

          <dl className="mt-5 grid grid-cols-2 divide-x divide-gray-200 rounded-xl border border-gray-200 bg-gray-50/60">
            <Stat
              label="Department"
              value={offer.department_name || 'Not specified'}
            />
            <Stat
              label="Experience"
              value={formatExperience(offer.job_experience)}
            />
          </dl>
          <div className="mt-3 flex items-center justify-between rounded-xl border border-gray-200 px-4 py-3">
            <span className="flex items-center gap-2 text-[13px] text-gray-500">
              <Wallet className="h-4 w-4 text-gray-400" strokeWidth={1.75} />
              Salary range
            </span>
            <span className="text-sm font-semibold text-gray-900">
              {formatSalary(offer.salary_min_bdt, offer.salary_max_bdt)}
            </span>
          </div>
        </div>

        <div className="flex min-h-[60px] items-center justify-between gap-3 border-t border-gray-100 px-5 py-2.5 sm:px-6">
          <p className="min-w-0 text-[13px] leading-5 text-gray-500">
            {(STATUS[offer.offer_letter_status] ?? STATUS.pending).note}
          </p>
          {isPending ? (
            <Button className="btn-primary h-9 shrink-0 rounded-full px-4 text-[13px] font-medium">
              Review offer
              <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          ) : (
            <span className="inline-flex shrink-0 items-center gap-1.5 text-[13px] font-medium text-theme-primary">
              <FileText className="h-4 w-4" />
              View letter
            </span>
          )}
        </div>
      </article>

      {/* Detail Modal */}
      <Modal
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        centered
        width={680}
        title={null}
      >
        <WithLoader isLoading={isFetchingDetails || isUpdating}>
          {details ? (
            <div className="pt-1">
              {/* Header */}
              <div className="flex items-start gap-4 pr-8">
                <OrgAvatar name={details.organization_name} />
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusBadge status={offer.offer_letter_status} />
                    <span className="text-xs text-gray-400">
                      {formatDate(details.created_at)}
                    </span>
                  </div>
                  <h2 className="mt-2 text-lg font-semibold leading-snug text-gray-900">
                    {details.offer_letter_title}
                  </h2>
                  <p className="mt-0.5 text-sm text-gray-500">
                    {details.organization_name} · {details.job_title}
                  </p>
                </div>
              </div>

              {/* Body */}
              <div className="mt-5 max-h-[50vh] overflow-y-auto rounded-xl border border-gray-200 bg-gray-50/70 p-5">
                <div
                  className="ql-editor !p-0 text-sm leading-relaxed text-gray-700"
                  dangerouslySetInnerHTML={{
                    __html: details.offer_letter_body,
                  }}
                />
              </div>

              {/* Footer */}
              <div className="mt-5 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
                {details.email_attachment ? (
                  <a
                    href={details.email_attachment}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 text-sm font-medium text-theme-primary hover:underline"
                  >
                    <FileDown className="h-4 w-4" />
                    Download PDF
                  </a>
                ) : (
                  <span />
                )}

                {isPending && (
                  <div className="flex gap-2">
                    <Button
                      danger
                      onClick={() => handleStatusUpdate('canceled')}
                      className="h-10 flex-1 rounded-full px-5 font-medium sm:flex-none"
                    >
                      Decline
                    </Button>
                    <Button
                      onClick={() => handleStatusUpdate('accepted')}
                      className="btn-primary h-10 flex-1 rounded-full px-5 font-medium sm:flex-none"
                    >
                      Accept offer
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            !isFetchingDetails && (
              <div className="py-10 text-center text-sm text-gray-500">
                Could not load offer letter details.
              </div>
            )
          )}
        </WithLoader>
      </Modal>
    </>
  );
};
